'use client';

import { useState } from 'react';
import ToolHero from '@/components/tools/ToolHero';
import ToolCTA from '@/components/tools/ToolCTA';
import ProgressBar from '@/components/tools/ProgressBar';
import { analyzeJDWithAI, type AIJDAnalysis } from '@/lib/tools/api';
import {
  Target, CheckCircle2, Sparkles, Wrench, Handshake, Building2,
  DollarSign, AlertTriangle, CircleCheck, Lightbulb, Briefcase,
  GraduationCap, ClipboardList,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface AnalysisResult {
  mustHave: string[];
  niceToHave: string[];
  hardSkills: string[];
  softSkills: string[];
  experienceLevel: {
    level: string;
    yearsDetected: string | null;
    confidence: string;
  };
  cultureSignals: CultureSignal[];
  salaryClues: string[];
  redFlags: FlagItem[];
  greenFlags: FlagItem[];
  demandScore: number;
  tips: string[];
}

interface CultureSignal {
  label: string;
  evidence: string;
  sentiment: 'positive' | 'neutral' | 'caution';
}

interface FlagItem {
  label: string;
  evidence: string;
  explanation: string;
}

/* ------------------------------------------------------------------ */
/*  Hard-skill dictionaries                                            */
/* ------------------------------------------------------------------ */

const PROGRAMMING_LANGUAGES = [
  'javascript', 'typescript', 'python', 'java', 'c\\+\\+', 'c#', 'go',
  'golang', 'rust', 'ruby', 'php', 'swift', 'kotlin', 'scala', 'r\\b',
  'matlab', 'perl', 'objective-c', 'dart', 'elixir', 'clojure',
  'haskell', 'lua', 'groovy', 'fortran', 'cobol', 'assembly',
  'visual basic', 'vb\\.net', 'f#', 'erlang', 'sql', 'nosql', 'plsql',
  'html', 'css', 'sass', 'less', 'graphql', 'solidity', 'apex',
];

const TOOLS_AND_FRAMEWORKS = [
  'react', 'angular', 'vue', 'svelte', 'next\\.js', 'nextjs', 'nuxt',
  'express', 'django', 'flask', 'spring', 'rails', 'laravel', 'node\\.js',
  'nodejs', 'deno', 'bun', 'webpack', 'vite', 'docker', 'kubernetes',
  'k8s', 'terraform', 'ansible', 'jenkins', 'github actions', 'gitlab ci',
  'circleci', 'aws', 'azure', 'gcp', 'google cloud', 'heroku', 'vercel',
  'netlify', 'cloudflare', 'firebase', 'supabase', 'redis', 'kafka',
  'rabbitmq', 'elasticsearch', 'mongodb', 'postgresql', 'postgres', 'mysql',
  'dynamodb', 'cassandra', 'snowflake', 'databricks', 'airflow',
  'spark', 'hadoop', 'tableau', 'power bi', 'looker', 'dbt',
  'figma', 'sketch', 'adobe xd', 'photoshop', 'illustrator', 'jira',
  'confluence', 'asana', 'trello', 'linear', 'notion', 'slack',
  'salesforce', 'hubspot', 'marketo', 'segment', 'amplitude',
  'mixpanel', 'datadog', 'splunk', 'new relic', 'grafana',
  'prometheus', 'sentry', 'pagerduty', 'tailwind', 'bootstrap',
  'material ui', 'storybook', 'cypress', 'playwright', 'selenium',
  'jest', 'mocha', 'pytest', 'junit', 'terraform', 'pulumi',
  'openai', 'langchain', 'pytorch', 'tensorflow', 'scikit-learn',
  'pandas', 'numpy', 'matplotlib', 'jupyter', 'git', 'linux',
  'rest api', 'restful', 'microservices', 'ci/cd', 'agile', 'scrum',
  'sap', 'oracle', 'servicenow',
];

const CERTIFICATIONS = [
  'aws certified', 'azure certified', 'gcp certified', 'pmp',
  'cissp', 'cisa', 'cism', 'comptia', 'ccna', 'ccnp', 'ccie',
  'itil', 'togaf', 'six sigma', 'scrum master', 'csm', 'psm',
  'safe', 'prince2', 'cpa', 'cfa', 'frm', 'series 7', 'series 63',
  'google analytics certified', 'hubspot certified',
  'salesforce certified', 'microsoft certified', 'certified',
  'certification', 'license', 'licensed',
];

const SOFT_SKILLS_LIST = [
  'communication', 'leadership', 'teamwork', 'collaboration',
  'problem.solving', 'critical thinking', 'analytical', 'creative',
  'creativity', 'adaptability', 'flexibility', 'time management',
  'organization', 'organizational', 'attention to detail',
  'detail.oriented', 'self.motivated', 'self.starter', 'proactive',
  'interpersonal', 'negotiation', 'presentation', 'public speaking',
  'mentoring', 'coaching', 'empathy', 'emotional intelligence',
  'conflict resolution', 'decision.making', 'strategic thinking',
  'strategic planning', 'stakeholder management', 'cross.functional',
  'multitasking', 'prioritization', 'initiative', 'accountability',
  'ownership', 'resilience', 'customer.focused', 'customer service',
  'relationship building', 'networking', 'facilitation',
  'influencing', 'persuasion', 'active listening',
];

/* ------------------------------------------------------------------ */
/*  Extraction helpers                                                 */
/* ------------------------------------------------------------------ */

function matchTerms(text: string, terms: string[]): string[] {
  const found: string[] = [];
  const lower = text.toLowerCase();
  for (const term of terms) {
    try {
      const regex = new RegExp(`\\b${term}\\b`, 'i');
      if (regex.test(lower)) {
        // Clean up display name: remove regex escapes
        const display = term
          .replace(/\\\+/g, '+')
          .replace(/\\\./g, '.')
          .replace(/\\b/g, '')
          .replace(/\./g, ' ')
          .split(' ')
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ');
        if (!found.includes(display)) {
          found.push(display);
        }
      }
    } catch {
      // Skip invalid regex
    }
  }
  return found;
}

function isBoilerplateLine(line: string): boolean {
  // Very long non-bullet paragraphs are likely company descriptions
  if (line.length > 250) return true;

  const boilerplatePatterns = [
    /\bis one of the world/i,
    /\bour mission is\b/i,
    /\bmillion (paid )?(members|users|customers|subscribers)\b/i,
    /\b\d+ countries\b/i,
    /\bfounded in \d{4}\b/i,
    /\bheadquartered in\b/i,
    /\bequal opportunity employer\b/i,
    /\bwe (believe|value|strive|aim|seek to)\b/i,
    /\b(our|the) (company|organization|firm) (is|was|has)\b/i,
    /\bjoin (us|our team)\b.*\b(build|create|shape|transform)\b/i,
    /\bwe are (a |an |the )?(leading|global|world|premier|top)\b/i,
    /\bconnecting (members|people|users|customers) to\b/i,
    /\bserving (millions|customers|users|clients) (in|across|around)\b/i,
    /\bapply now\b/i,
    /\blearn more at\b/i,
  ];

  return boilerplatePatterns.some((p) => p.test(line));
}

function extractRequirements(text: string): {
  mustHave: string[];
  niceToHave: string[];
} {
  const mustHave: string[] = [];
  const niceToHave: string[] = [];

  // Split into lines/sentences for line-by-line analysis
  const lines = text.split(/[\n\r]+/).map((l) => l.trim()).filter(Boolean);

  // Track which section we are in
  let currentSection: 'must' | 'nice' | 'unknown' = 'unknown';

  const mustSectionPatterns = [
    /\b(required|requirements|minimum qualifications|basic qualifications|must have|what you('ll)? need|what we('re)? looking for|you have|qualifications)\b/i,
  ];

  const niceSectionPatterns = [
    /\b(preferred|nice to have|bonus|plus|desired|ideally|additionally|it('s| is) a plus|good to have|not required|optional|extra credit|stand out)\b/i,
  ];

  // Sections that are NOT requirements — reset to unknown
  const neutralSectionPatterns = [
    /\b(about (us|the (company|role|team|opportunity|position))|company (overview|description|profile)|who we are|the opportunity|overview of|our (mission|company|story|culture)|job (summary|description|overview)|role (summary|overview|description)|what (we|you('ll)?) (do|offer|provide|get)|responsibilities|about this role)\b/i,
  ];

  const mustLinePatterns = [
    /\b(required|must have|must be|need to have|essential|mandatory|minimum)\b/i,
  ];

  const niceLinePatterns = [
    /\b(prefer(red)?|nice to have|bonus|plus|ideal(ly)?|desired|a plus|not required|optional|advantageous|asset|helpful|familiarity with)\b/i,
  ];

  for (const line of lines) {
    // Detect neutral/about sections and reset to unknown
    const isNeutralSection = neutralSectionPatterns.some((p) => p.test(line));
    if (isNeutralSection && !mustSectionPatterns.some((p) => p.test(line))) {
      currentSection = 'unknown';
      continue;
    }

    // Detect section headers
    const isMustSection = mustSectionPatterns.some((p) => p.test(line));
    const isNiceSection = niceSectionPatterns.some((p) => p.test(line));

    if (isMustSection && !isNiceSection) {
      currentSection = 'must';
      continue;
    }
    if (isNiceSection) {
      currentSection = 'nice';
      continue;
    }

    // Skip lines that are too short (section headers, etc.) or are non-requirement lines
    const isBullet = /^[\-\*\u2022\u25CB\u25CF\u25AA\u25AB\u2013\u2014]/.test(line) ||
      /^\d+[\.\)]/.test(line);
    const isSubstantive = line.length > 15;

    if (!isBullet && !isSubstantive) continue;

    // Clean the bullet character
    const cleaned = line.replace(/^[\-\*\u2022\u25CB\u25CF\u25AA\u25AB\u2013\u2014\d\.\)]+\s*/, '').trim();
    if (!cleaned || cleaned.length < 10) continue;

    // Skip boilerplate / company description lines
    if (isBoilerplateLine(cleaned)) continue;

    // Skip lines that look like job titles (short, no verb-like requirement language)
    if (cleaned.length < 80 && /^[A-Z]/.test(cleaned) && !isBullet &&
        /\b(architect|engineer|manager|director|analyst|developer|designer|lead|principal|senior|specialist|coordinator|consultant|administrator|officer)\b/i.test(cleaned) &&
        !/\b(experience|knowledge|ability|skill|proficien|familiar|understand|year)\b/i.test(cleaned)) {
      continue;
    }

    // Check line-level signals
    const lineIsNice = niceLinePatterns.some((p) => p.test(cleaned));
    const lineIsMust = mustLinePatterns.some((p) => p.test(cleaned));

    if (lineIsNice && !lineIsMust) {
      niceToHave.push(cleaned);
    } else if (lineIsMust) {
      mustHave.push(cleaned);
    } else if (currentSection === 'nice') {
      niceToHave.push(cleaned);
    } else if (currentSection === 'must') {
      mustHave.push(cleaned);
    }
    // Lines in 'unknown' section with no line-level signals are skipped
    // to avoid cluttering results with non-requirement lines
  }

  return { mustHave, niceToHave };
}

function detectExperienceLevel(text: string): {
  level: string;
  yearsDetected: string | null;
  confidence: string;
} {
  const lower = text.toLowerCase();

  // Extract year ranges
  const yearPatterns = [
    /(\d+)\s*\+?\s*years?\s*(of\s+)?(experience|exp)/i,
    /(\d+)\s*-\s*(\d+)\s*years?\s*(of\s+)?(experience|exp)/i,
    /minimum\s+(\d+)\s*years/i,
    /at\s+least\s+(\d+)\s*years/i,
  ];

  let maxYears = 0;
  let yearsString: string | null = null;

  for (const pattern of yearPatterns) {
    const match = text.match(pattern);
    if (match) {
      yearsString = match[0];
      const numbers = match[0].match(/\d+/g);
      if (numbers) {
        const highest = Math.max(...numbers.map(Number));
        if (highest > maxYears) maxYears = highest;
      }
    }
  }

  // Also check for title-level clues
  const hasExecutiveSignals = /\b(vp|vice president|director|head of|chief|c-suite|cto|cfo|coo|cio|cmo|svp|evp)\b/i.test(lower);
  const hasSeniorSignals = /\b(senior|sr\.|lead|principal|staff|architect|manager)\b/i.test(lower);
  const hasMidSignals = /\b(mid.level|intermediate|experienced)\b/i.test(lower);
  const hasEntrySignals = /\b(entry.level|junior|jr\.|intern|internship|graduate|new grad|associate|trainee|apprentice|0-2 years|1-2 years|0-1 years)\b/i.test(lower);

  let level: string;
  let confidence: string;

  if (hasExecutiveSignals || maxYears >= 15) {
    level = 'Executive / Director';
    confidence = hasExecutiveSignals && maxYears >= 15 ? 'High' : 'Medium';
  } else if (maxYears >= 8 || (hasSeniorSignals && maxYears >= 5)) {
    level = 'Senior';
    confidence = hasSeniorSignals ? 'High' : 'Medium';
  } else if (maxYears >= 3 || hasMidSignals || (hasSeniorSignals && maxYears === 0)) {
    level = hasSeniorSignals ? 'Senior' : 'Mid-Level';
    confidence = hasMidSignals || maxYears >= 3 ? 'Medium' : 'Low';
  } else if (hasEntrySignals || maxYears <= 2) {
    level = 'Entry-Level';
    confidence = hasEntrySignals ? 'High' : maxYears > 0 ? 'Medium' : 'Low';
  } else {
    level = 'Mid-Level';
    confidence = 'Low';
  }

  // If no signals at all, mark as unclear
  if (!yearsString && !hasExecutiveSignals && !hasSeniorSignals && !hasMidSignals && !hasEntrySignals) {
    level = 'Not Specified';
    confidence = 'N/A';
  }

  return { level, yearsDetected: yearsString, confidence };
}

function detectCultureSignals(text: string): CultureSignal[] {
  const signals: CultureSignal[] = [];

  const culturePatterns: {
    pattern: RegExp;
    label: string;
    sentiment: CultureSignal['sentiment'];
  }[] = [
    { pattern: /\b(remote|work from home|wfh|distributed team|remote.first|fully remote|location.flexible)\b/i, label: 'Remote-Friendly', sentiment: 'positive' },
    { pattern: /\b(hybrid|in.office \d|days? (in|at) (the )?office)\b/i, label: 'Hybrid Work Model', sentiment: 'neutral' },
    { pattern: /\b(on.site|in.person|in.office|office.based|must be located|relocation)\b/i, label: 'On-Site Required', sentiment: 'neutral' },
    { pattern: /\b(fast.paced|rapidly growing|high.growth|move fast|hustle|scrappy|wear many hats)\b/i, label: 'Fast-Paced / Startup Culture', sentiment: 'caution' },
    { pattern: /\b(work.life balance|flexible (hours|schedule)|generous (pto|vacation|time off)|unlimited (pto|vacation|time off)|mental health)\b/i, label: 'Work-Life Balance Focus', sentiment: 'positive' },
    { pattern: /\b(diversity|inclusion|dei|belonging|equit(y|able)|inclusive|equal opportunity|underrepresented)\b/i, label: 'DEI Commitment', sentiment: 'positive' },
    { pattern: /\b(fortune \d|enterprise|established|global (company|organization|firm)|multinational|publicly traded|fortune 500)\b/i, label: 'Large / Corporate Environment', sentiment: 'neutral' },
    { pattern: /\b(startup|early.stage|series [a-d]|pre.seed|seed stage|venture.backed|funded by)\b/i, label: 'Startup Environment', sentiment: 'neutral' },
    { pattern: /\b(collaborative|team.oriented|cross.functional|open door|flat (structure|hierarchy|organization))\b/i, label: 'Collaborative Culture', sentiment: 'positive' },
    { pattern: /\b(innovation|innovative|cutting.edge|bleeding edge|state of the art|latest technologies|emerging tech)\b/i, label: 'Innovation-Focused', sentiment: 'positive' },
    { pattern: /\b(learning|professional development|tuition|education (benefit|reimbursement|stipend)|conference|training budget|grow(th)? opportunit)\b/i, label: 'Learning & Development', sentiment: 'positive' },
    { pattern: /\b(transparent|transparency|open (communication|book)|candid|honest feedback)\b/i, label: 'Transparency-Oriented', sentiment: 'positive' },
    { pattern: /\b(mission.driven|purpose.driven|social impact|making a difference|change the world|meaningful work)\b/i, label: 'Mission-Driven', sentiment: 'positive' },
    { pattern: /\b(competitive|results.driven|performance.based|meritocrac|top performer|high.performer|ambitious)\b/i, label: 'Performance-Driven', sentiment: 'neutral' },
    { pattern: /\b(overtime|weekends|on.call|24\/7|after.hours|crunch|extended hours)\b/i, label: 'Extended Hours Expected', sentiment: 'caution' },
  ];

  for (const { pattern, label, sentiment } of culturePatterns) {
    const match = text.match(pattern);
    if (match) {
      signals.push({ label, evidence: match[0], sentiment });
    }
  }

  return signals;
}

function detectSalaryClues(text: string): string[] {
  const clues: string[] = [];

  // Direct salary mentions
  const salaryRange = text.match(/\$[\d,]+\s*[-\u2013\u2014to]+\s*\$[\d,]+(\s*(per|\/)\s*(year|hour|hr|annum|month))?/gi);
  if (salaryRange) {
    salaryRange.forEach((m) => clues.push(`Salary range mentioned: ${m.trim()}`));
  }

  const singleSalary = text.match(/\$[\d,]{4,}(\s*(per|\/)\s*(year|hour|hr|annum|month))?/gi);
  if (singleSalary && !salaryRange) {
    singleSalary.forEach((m) => clues.push(`Compensation figure found: ${m.trim()}`));
  }

  // K-style salary
  const kSalary = text.match(/\$\d+k\s*[-\u2013\u2014to]+\s*\$?\d+k/gi);
  if (kSalary) {
    kSalary.forEach((m) => clues.push(`Salary range: ${m.trim()}`));
  }

  // Benefits language
  const benefitPatterns: { pattern: RegExp; label: string }[] = [
    { pattern: /\b(stock options?|equity|rsu|restricted stock)\b/i, label: 'Equity / stock options mentioned' },
    { pattern: /\b(401k|401\(k\)|retirement plan|pension)\b/i, label: 'Retirement benefits offered' },
    { pattern: /\b(health (insurance|benefits|coverage)|medical|dental|vision)\b/i, label: 'Health insurance mentioned' },
    { pattern: /\b(bonus|annual bonus|performance bonus|signing bonus|sign-on bonus)\b/i, label: 'Bonus structure mentioned' },
    { pattern: /\b(unlimited (pto|vacation|time off)|generous (pto|vacation)|\d+\s*days?\s*(of\s+)?(pto|vacation|paid time off))\b/i, label: 'PTO / vacation benefits mentioned' },
    { pattern: /\b(parental leave|maternity|paternity|family leave)\b/i, label: 'Parental leave offered' },
    { pattern: /\b(tuition (reimbursement|assistance)|education (benefit|stipend)|student loan)\b/i, label: 'Education benefits mentioned' },
    { pattern: /\b(competitive (compensation|salary|pay)|market rate|above.market)\b/i, label: '"Competitive compensation" language (often vague)' },
    { pattern: /\b(commission|variable (pay|compensation)|ote|on.target earnings)\b/i, label: 'Commission / variable pay structure' },
    { pattern: /\b(relocation (assistance|package|bonus|support))\b/i, label: 'Relocation assistance offered' },
    { pattern: /\b(gym|wellness (benefit|stipend|program)|fitness)\b/i, label: 'Wellness / gym benefits' },
  ];

  for (const { pattern, label } of benefitPatterns) {
    if (pattern.test(text)) {
      clues.push(label);
    }
  }

  return clues;
}

function detectRedFlags(text: string): FlagItem[] {
  const flags: FlagItem[] = [];

  const redFlagPatterns: { pattern: RegExp; label: string; explanation: string }[] = [
    {
      pattern: /\bwear many hats\b/i,
      label: '"Wear Many Hats"',
      explanation: 'Often means understaffed. You may be doing the work of 2-3 people without proportional pay.',
    },
    {
      pattern: /\b(rockstar|ninja|guru|wizard|unicorn|superstar|10x)\b/i,
      label: 'Hyperbolic Title Language',
      explanation: 'Terms like "rockstar" or "ninja" can signal unrealistic expectations or an immature company culture.',
    },
    {
      pattern: /\bwe('re| are) (like )?(a )?family\b/i,
      label: '"We\'re Like a Family"',
      explanation: 'This language often masks poor boundaries, expected overtime, and difficulty saying no to unreasonable requests.',
    },
    {
      pattern: /\b(work hard,? play hard|hustle culture)\b/i,
      label: '"Work Hard, Play Hard"',
      explanation: 'Usually translates to long hours. The "play hard" part rarely compensates for the burnout.',
    },
    {
      pattern: /\b(must be available|24\/7|on.call|weekends? (required|expected)|after.hours)\b/i,
      label: 'Always-On Expectations',
      explanation: 'Signals that work-life balance boundaries may not be respected. Clarify on-call expectations.',
    },
    {
      pattern: /\b(fast.paced)[\s\S]*(fast.paced)/i,
      label: 'Repeated "Fast-Paced" Emphasis',
      explanation: 'Mentioned multiple times suggests a genuinely chaotic environment rather than standard corporate language.',
    },
    {
      pattern: /\b(salary|compensation|pay).{0,20}(commensurate|based on|depending on|negotiable|competitive)\b/i,
      label: 'Vague Compensation Language',
      explanation: 'No specific range given. This can mean below-market pay or wildly varying expectations.',
    },
    {
      pattern: /\b(\d{2,})\+?\s*years?\s*(of\s+)?(experience|exp)/i,
      label: 'Unusually High Experience Requirement',
      explanation: 'Extremely high year requirements may indicate the listing was written around a specific internal candidate or has inflated expectations.',
    },
    {
      pattern: /\b(other duties as assigned|additional responsibilities|and more)\b/i,
      label: '"Other Duties as Assigned"',
      explanation: 'A catch-all that can be used to pile on work outside the described role.',
    },
    {
      pattern: /\b(unpaid|volunteer|exposure|experience (in lieu|instead) of (pay|compensation))\b/i,
      label: 'No Pay / Exposure-Based',
      explanation: 'Work should be compensated. "Paying in exposure" is not acceptable for professional roles.',
    },
    {
      pattern: /\b(immediately|asap|urgent(ly)?|yesterday|start right away)\b/i,
      label: 'Extreme Urgency',
      explanation: 'Urgency can indicate someone quit suddenly, high turnover, or poor planning. Ask why the role is open.',
    },
  ];

  for (const { pattern, label, explanation } of redFlagPatterns) {
    const match = text.match(pattern);
    if (match) {
      // Special check for years: only flag 15+
      if (label === 'Unusually High Experience Requirement') {
        const years = parseInt(match[1], 10);
        if (years < 15) continue;
      }
      flags.push({ label, evidence: match[0].trim(), explanation });
    }
  }

  // Count total distinct skill requirements to detect "too many hats"
  const allSkills = [
    ...matchTerms(text, PROGRAMMING_LANGUAGES),
    ...matchTerms(text, TOOLS_AND_FRAMEWORKS),
  ];
  if (allSkills.length > 20) {
    flags.push({
      label: 'Excessive Skill Requirements',
      evidence: `${allSkills.length} distinct technical skills listed`,
      explanation: 'An unrealistic number of required skills may indicate the employer does not understand the role or expects one person to do everything.',
    });
  }

  return flags;
}

function detectGreenFlags(text: string): FlagItem[] {
  const flags: FlagItem[] = [];

  const greenFlagPatterns: { pattern: RegExp; label: string; explanation: string }[] = [
    {
      pattern: /\b(mentorship|mentor(ing)?|buddy (program|system)|onboarding program)\b/i,
      label: 'Mentorship / Onboarding',
      explanation: 'Structured support for new hires signals investment in employee success.',
    },
    {
      pattern: /\b(professional development|career (growth|advancement|path)|promotion|growth opportunit(y|ies))\b/i,
      label: 'Career Growth Opportunities',
      explanation: 'Explicit mention of growth paths suggests the company invests in employee development.',
    },
    {
      pattern: /\b(clear (expectations|responsibilities|deliverables)|well.defined (role|scope))\b/i,
      label: 'Clear Role Definition',
      explanation: 'Well-defined responsibilities reduce ambiguity and scope creep.',
    },
    {
      pattern: /\b(work.life balance|flexible (hours|schedule|work)|no (weekend|overtime)|respect.{0,10}(time|boundaries))\b/i,
      label: 'Work-Life Balance Commitment',
      explanation: 'Explicit balance language suggests the company values sustainable work patterns.',
    },
    {
      pattern: /\b(parental leave|maternity|paternity|family leave|caregiver)\b/i,
      label: 'Family-Friendly Benefits',
      explanation: 'Parental leave and family support indicate a mature benefits structure.',
    },
    {
      pattern: /\b(equity|stock options?|rsu|ownership stake|profit sharing)\b/i,
      label: 'Equity / Ownership',
      explanation: 'Offering equity aligns employee and company interests and can be a significant financial benefit.',
    },
    {
      pattern: /\b(diversity|inclusion|dei|belonging|equit(y|able) (hiring|workplace)|equal opportunity|underrepresented)\b/i,
      label: 'Diversity & Inclusion Focus',
      explanation: 'Active DEI commitment suggests a more welcoming and equitable work environment.',
    },
    {
      pattern: /\b(unlimited (pto|vacation)|generous (pto|vacation|time off)|\d{3,}\s*hours?\s*(of\s+)?(pto|vacation)|(\d{2,})\s*days?\s*(of\s+)?(pto|vacation|paid time off))\b/i,
      label: 'Strong PTO Benefits',
      explanation: 'Generous time-off policies indicate the company values rest and recovery.',
    },
    {
      pattern: /\b(401k match|employer match|retirement (match|contribution))\b/i,
      label: '401k Match',
      explanation: 'Employer matching on retirement is a concrete financial benefit that adds to total compensation.',
    },
    {
      pattern: /\b(fully remote|remote.first|work from anywhere|location.flexible|distributed team)\b/i,
      label: 'Remote-First / Location Flexible',
      explanation: 'True remote flexibility gives you freedom in where you live and how you structure your day.',
    },
    {
      pattern: /\b(transparent|transparency|open (salary|compensation)|pay band|salary (range|band).*\$)\b/i,
      label: 'Pay Transparency',
      explanation: 'Sharing salary ranges upfront is a green flag for fair compensation practices.',
    },
    {
      pattern: /\b(learning (budget|stipend)|conference (budget|attendance)|education (reimbursement|stipend|benefit)|training budget)\b/i,
      label: 'Learning & Development Budget',
      explanation: 'Dedicated budget for learning shows investment in keeping employees current and growing.',
    },
    {
      pattern: /\b(no (meetings?|meeting).{0,15}(day|friday)|focus time|deep work|async(hronous)?)\b/i,
      label: 'Focus Time / Async Culture',
      explanation: 'Companies that protect focus time tend to respect productivity and reduce meeting fatigue.',
    },
  ];

  for (const { pattern, label, explanation } of greenFlagPatterns) {
    const match = text.match(pattern);
    if (match) {
      flags.push({ label, evidence: match[0].trim(), explanation });
    }
  }

  return flags;
}

function calculateDemandScore(result: Omit<AnalysisResult, 'demandScore' | 'tips'>): number {
  let score = 50; // Start neutral

  // More must-haves = more demanding
  score += Math.min(result.mustHave.length * 3, 20);

  // Many hard skills = more demanding
  score += Math.min(result.hardSkills.length * 2, 15);

  // Senior+ experience level adds demand
  if (result.experienceLevel.level === 'Senior') score += 10;
  if (result.experienceLevel.level === 'Executive / Director') score += 15;

  // More red flags = more demanding / concerning
  score += result.redFlags.length * 5;

  // Green flags soften demand perception slightly
  score -= Math.min(result.greenFlags.length * 2, 10);

  // Nice-to-haves are less demanding than must-haves
  score += Math.min(result.niceToHave.length * 1, 5);

  // Certifications add demand
  const certs = matchTerms(
    result.hardSkills.join(' '),
    ['certified', 'certification']
  );
  score += certs.length * 3;

  return Math.max(10, Math.min(100, Math.round(score)));
}

function generateTips(result: AnalysisResult): string[] {
  const tips: string[] = [];

  if (result.mustHave.length === 0 && result.niceToHave.length === 0) {
    tips.push('This job description lacks clear requirement categories. Ask the recruiter for a concrete list of must-have qualifications.');
  }

  if (result.mustHave.length > 0 && result.niceToHave.length > 0) {
    tips.push(`Focus your resume on the ${result.mustHave.length} must-have requirements first. Nice-to-haves can differentiate you but are not dealbreakers.`);
  }

  if (result.hardSkills.length > 10) {
    tips.push('This listing has many technical requirements. You do not need to match 100% - aim for 70%+ of the hard skills and show willingness to learn the rest.');
  }

  if (result.redFlags.length > 3) {
    tips.push('Multiple red flags detected. Prepare pointed interview questions about work-life balance, team size, and why the role is open.');
  }

  if (result.redFlags.length === 0 && result.greenFlags.length > 2) {
    tips.push('This listing has several positive signals and no red flags. It appears to be a well-written job description from a considerate employer.');
  }

  if (result.salaryClues.length === 0) {
    tips.push('No salary information provided. Research market rates on Glassdoor, Levels.fyi, or Payscale before the interview to anchor your negotiation.');
  }

  if (result.salaryClues.some((c) => c.includes('Competitive compensation'))) {
    tips.push('"Competitive compensation" is deliberately vague. Always ask for the specific range early in the process to avoid wasting time.');
  }

  if (result.experienceLevel.level === 'Not Specified') {
    tips.push('No clear experience level detected. Check if the title gives clues, or ask the recruiter about the expected seniority for this role.');
  }

  if (result.cultureSignals.some((s) => s.label === 'Fast-Paced / Startup Culture')) {
    tips.push('"Fast-paced" often means you will be expected to do more with less. Ask about team size relative to workload during the interview.');
  }

  if (result.softSkills.length > 5) {
    tips.push('Many soft skills mentioned. Weave behavioral examples (STAR method) into your cover letter and interview prep for the top ones listed.');
  }

  // Always add at least one general tip
  if (tips.length === 0) {
    tips.push('Tailor your resume to mirror the exact language used in this job description. ATS systems and recruiters look for keyword matches.');
  }

  tips.push('Use the exact phrases from this job description in your resume. Mirroring their language increases your chances of passing both ATS filters and human reviewers.');

  return tips;
}

/* ------------------------------------------------------------------ */
/*  Main analysis function                                             */
/* ------------------------------------------------------------------ */

function analyzeJobDescription(text: string): AnalysisResult {
  const { mustHave, niceToHave } = extractRequirements(text);
  const hardSkills = [
    ...matchTerms(text, PROGRAMMING_LANGUAGES),
    ...matchTerms(text, TOOLS_AND_FRAMEWORKS),
    ...matchTerms(text, CERTIFICATIONS),
  ];
  const softSkills = matchTerms(text, SOFT_SKILLS_LIST);
  const experienceLevel = detectExperienceLevel(text);
  const cultureSignals = detectCultureSignals(text);
  const salaryClues = detectSalaryClues(text);
  const redFlags = detectRedFlags(text);
  const greenFlags = detectGreenFlags(text);

  const partial = {
    mustHave,
    niceToHave,
    hardSkills,
    softSkills,
    experienceLevel,
    cultureSignals,
    salaryClues,
    redFlags,
    greenFlags,
  };

  const demandScore = calculateDemandScore(partial);

  const result: AnalysisResult = { ...partial, demandScore, tips: [] };
  result.tips = generateTips(result);

  return result;
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

function SectionCard({
  title,
  icon: Icon,
  children,
  variant = 'blue',
}: {
  title: string;
  icon: LucideIcon;
  children: React.ReactNode;
  variant?: 'blue' | 'green' | 'red' | 'amber' | 'purple' | 'gray';
}) {
  const borderColors = {
    blue: 'border-blue-200',
    green: 'border-green-200',
    red: 'border-red-200',
    amber: 'border-amber-200',
    purple: 'border-purple-200',
    gray: 'border-gray-200',
  };

  const headerColors = {
    blue: 'text-blue-800',
    green: 'text-green-800',
    red: 'text-red-800',
    amber: 'text-amber-800',
    purple: 'text-purple-800',
    gray: 'text-gray-800',
  };

  return (
    <div className={`bg-white rounded-2xl shadow-lg border ${borderColors[variant]} p-6`}>
      <h3 className={`text-lg font-bold ${headerColors[variant]} flex items-center gap-2 mb-4`}>
        <Icon className="w-5 h-5 flex-shrink-0" />
        {title}
      </h3>
      {children}
    </div>
  );
}

function TagBadge({
  text,
  variant = 'blue',
}: {
  text: string;
  variant?: 'blue' | 'green' | 'red' | 'amber' | 'purple' | 'gray';
}) {
  const styles = {
    blue: 'bg-blue-50 text-blue-700 border border-blue-200',
    green: 'bg-green-50 text-green-700 border border-green-200',
    red: 'bg-red-50 text-red-700 border border-red-200',
    amber: 'bg-amber-50 text-amber-700 border border-amber-200',
    purple: 'bg-purple-50 text-purple-700 border border-purple-200',
    gray: 'bg-gray-50 text-gray-700 border border-gray-200',
  };

  return (
    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${styles[variant]}`}>
      {text}
    </span>
  );
}

function FlagCard({
  item,
  variant,
}: {
  item: FlagItem;
  variant: 'red' | 'green';
}) {
  const bgColor = variant === 'red' ? 'bg-red-50' : 'bg-green-50';
  const borderColor = variant === 'red' ? 'border-red-200' : 'border-green-200';
  const labelColor = variant === 'red' ? 'text-red-800' : 'text-green-800';
  const evidenceColor = variant === 'red' ? 'text-red-700' : 'text-green-700';
  const headingColor = variant === 'red' ? 'text-red-700' : 'text-green-700';

  return (
    <div className={`${bgColor} border ${borderColor} rounded-xl p-4 mb-3`}>
      <div className="mb-2">
        <span className={`${labelColor} font-bold text-base`}>{item.label}</span>
        <span className={`ml-2 text-sm italic ${evidenceColor}`}>
          &quot;{item.evidence}&quot;
        </span>
      </div>
      <div className="text-sm">
        <span className={`font-semibold ${headingColor}`}>What this means: </span>
        <span className="text-gray-700">{item.explanation}</span>
      </div>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <p className="text-gray-500 text-sm italic">{message}</p>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export default function JDAnalyzerContent() {
  const [jobDescription, setJobDescription] = useState('');
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [hasAnalyzed, setHasAnalyzed] = useState(false);
  const [aiResult, setAiResult] = useState<AIJDAnalysis | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  function handleAnalyze() {
    if (!jobDescription.trim()) return;
    const analysis = analyzeJobDescription(jobDescription);
    setResult(analysis);
    setHasAnalyzed(true);
  }

  function handleReset() {
    setJobDescription('');
    setResult(null);
    setHasAnalyzed(false);
    setAiResult(null);
    setAiError(null);
  }

  async function handleAIAnalysis() {
    if (!jobDescription.trim()) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const result = await analyzeJDWithAI(jobDescription);
      setAiResult(result);
    } catch (err) {
      setAiError(err instanceof Error ? err.message : 'AI analysis failed. Please try again.');
    } finally {
      setAiLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-stone-100">
      <ToolHero
        title="Job Description Analyzer"
        description="Decode any job posting to reveal requirements, culture signals, salary clues, and red flags. Understand what employers really want before you apply."
        category="Job Search"
      />

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Input Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-8">
          <label
            htmlFor="job-description"
            className="block text-lg font-bold text-gray-900 mb-3"
          >
            Paste the Job Description
          </label>
          <p className="text-sm text-gray-500 mb-4">
            Copy and paste the full job listing below. The more text you include,
            the deeper the analysis.
          </p>
          <textarea
            id="job-description"
            rows={14}
            value={jobDescription}
            onChange={(e) => setJobDescription(e.target.value)}
            placeholder="Paste the full job description here..."
            className="w-full border border-gray-300 rounded-xl p-4 text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y text-sm leading-relaxed"
          />

          <div className="flex items-center gap-3 mt-4">
            <button
              onClick={handleAnalyze}
              disabled={!jobDescription.trim()}
              className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-bold py-3 px-8 rounded-xl shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-lg"
            >
              Analyze Job Description
            </button>
            {hasAnalyzed && (
              <button
                onClick={handleReset}
                className="inline-flex items-center gap-2 px-6 py-3 border border-gray-300 rounded-xl text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Start Over
              </button>
            )}
          </div>
        </div>

        {/* Results */}
        {hasAnalyzed && result && (
          <div className="space-y-8">
            {/* Demand Score */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Posting Demand Score
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                How demanding is this job posting? Higher scores mean more requirements,
                more experience expected, or more red flags.
              </p>
              <ProgressBar
                value={result.demandScore}
                label={
                  result.demandScore < 40
                    ? 'Low Demand - Approachable posting'
                    : result.demandScore < 70
                      ? 'Moderate Demand - Standard expectations'
                      : 'High Demand - Very competitive posting'
                }
              />
            </div>

            {/* Experience Level */}
            <div className="bg-white rounded-2xl shadow-lg border border-blue-200 p-6">
              <h3 className="text-lg font-bold text-blue-800 flex items-center gap-2 mb-4">
                <Target className="w-5 h-5" />
                Experience Level Detected
              </h3>
              <div className="flex flex-wrap items-center gap-4">
                <div className="bg-blue-50 border border-blue-200 rounded-xl px-5 py-3">
                  <span className="text-2xl font-extrabold text-blue-700">
                    {result.experienceLevel.level}
                  </span>
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  {result.experienceLevel.yearsDetected && (
                    <p>
                      <span className="font-semibold text-gray-800">Years mentioned:</span>{' '}
                      {result.experienceLevel.yearsDetected}
                    </p>
                  )}
                  <p>
                    <span className="font-semibold text-gray-800">Confidence:</span>{' '}
                    {result.experienceLevel.confidence}
                  </p>
                </div>
              </div>
            </div>

            {/* Requirements: Must-Have vs Nice-to-Have */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <SectionCard title="Must-Have Requirements" icon={CheckCircle2} variant="green">
                {result.mustHave.length > 0 ? (
                  <ul className="space-y-2">
                    {result.mustHave.map((req, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="text-green-500 mt-0.5 flex-shrink-0">{'\u2022'}</span>
                        <span>{req}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <EmptyState message="No explicit must-have requirements detected. The job description may not clearly categorize its requirements." />
                )}
              </SectionCard>

              <SectionCard title="Nice-to-Have" icon={Sparkles} variant="purple">
                {result.niceToHave.length > 0 ? (
                  <ul className="space-y-2">
                    {result.niceToHave.map((req, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="text-purple-500 mt-0.5 flex-shrink-0">{'\u2022'}</span>
                        <span>{req}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <EmptyState message="No explicit nice-to-have requirements detected." />
                )}
              </SectionCard>
            </div>

            {/* Skills */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <SectionCard title={`Hard Skills (${result.hardSkills.length})`} icon={Wrench} variant="blue">
                {result.hardSkills.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {result.hardSkills.map((skill) => (
                      <TagBadge key={skill} text={skill} variant="blue" />
                    ))}
                  </div>
                ) : (
                  <EmptyState message="No hard skills detected in this job description." />
                )}
              </SectionCard>

              <SectionCard title={`Soft Skills (${result.softSkills.length})`} icon={Handshake} variant="amber">
                {result.softSkills.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {result.softSkills.map((skill) => (
                      <TagBadge key={skill} text={skill} variant="amber" />
                    ))}
                  </div>
                ) : (
                  <EmptyState message="No soft skills detected in this job description." />
                )}
              </SectionCard>
            </div>

            {/* Culture Signals */}
            <SectionCard title={`Culture Signals (${result.cultureSignals.length})`} icon={Building2} variant="blue">
              {result.cultureSignals.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {result.cultureSignals.map((signal, i) => {
                    const sentimentStyles = {
                      positive: 'bg-green-50 border-green-200 text-green-800',
                      neutral: 'bg-gray-50 border-gray-200 text-gray-800',
                      caution: 'bg-amber-50 border-amber-200 text-amber-800',
                    };
                    const dotStyles = {
                      positive: 'bg-green-400',
                      neutral: 'bg-gray-400',
                      caution: 'bg-amber-400',
                    };
                    return (
                      <div
                        key={i}
                        className={`border rounded-xl px-4 py-3 ${sentimentStyles[signal.sentiment]}`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`w-2 h-2 rounded-full ${dotStyles[signal.sentiment]}`} />
                          <span className="font-semibold text-sm">{signal.label}</span>
                        </div>
                        <p className="text-xs opacity-75 italic">
                          &quot;{signal.evidence}&quot;
                        </p>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <EmptyState message="No specific culture signals detected. The listing may be straightforward and factual." />
              )}
            </SectionCard>

            {/* Salary Clues */}
            <SectionCard title={`Salary & Benefits Clues (${result.salaryClues.length})`} icon={DollarSign} variant="green">
              {result.salaryClues.length > 0 ? (
                <ul className="space-y-2">
                  {result.salaryClues.map((clue, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className="text-green-500 mt-0.5 flex-shrink-0">{'\u2022'}</span>
                      <span>{clue}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState message="No salary or benefits information detected. This is common but worth asking about early in the process." />
              )}
            </SectionCard>

            {/* Red Flags & Green Flags */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-xl font-bold text-red-700 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" /> Red Flags ({result.redFlags.length})
                </h3>
                {result.redFlags.length > 0 ? (
                  result.redFlags.map((flag, i) => (
                    <FlagCard key={i} item={flag} variant="red" />
                  ))
                ) : (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="text-green-800 font-medium text-sm">
                      No red flags detected. This appears to be a straightforward listing.
                    </span>
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-xl font-bold text-green-700 mb-4 flex items-center gap-2">
                  <CircleCheck className="w-5 h-5" /> Green Flags ({result.greenFlags.length})
                </h3>
                {result.greenFlags.length > 0 ? (
                  result.greenFlags.map((flag, i) => (
                    <FlagCard key={i} item={flag} variant="green" />
                  ))
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex items-center gap-2">
                    <span className="text-gray-400">--</span>
                    <span className="text-gray-600 font-medium text-sm">
                      No green flags detected. This does not mean the job is bad, just that the listing does not highlight specific positives.
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Tips */}
            <SectionCard title="Reading Between the Lines" icon={Lightbulb} variant="gray">
              <ul className="space-y-3">
                {result.tips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-gray-700">
                    <span className="bg-blue-100 text-blue-700 font-bold rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs mt-0.5">
                      {i + 1}
                    </span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </SectionCard>

            {/* AI Deep Analysis */}
            {!aiResult && !aiLoading && (
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-2xl p-8 text-center">
                <h3 className="text-xl font-bold text-purple-900 mb-2">
                  Want a deeper analysis?
                </h3>
                <p className="text-sm text-purple-700 mb-4">
                  Our AI reads the full job description to extract structured requirements, education needs, and company insights that pattern matching can miss.
                </p>
                <button
                  onClick={handleAIAnalysis}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-bold py-3 px-8 rounded-xl shadow-lg transition-all duration-200 text-lg"
                >
                  Enhance with AI Analysis
                </button>
              </div>
            )}

            {aiLoading && (
              <div className="bg-white rounded-2xl shadow-lg border border-purple-200 p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-purple-200 border-t-purple-600 mb-4" />
                <p className="text-gray-600 font-medium">AI is analyzing the job description...</p>
                <p className="text-sm text-gray-400 mt-1">This usually takes 5-10 seconds</p>
              </div>
            )}

            {aiError && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
                <p className="text-red-700 font-medium mb-3">{aiError}</p>
                <button
                  onClick={handleAIAnalysis}
                  className="text-red-700 underline hover:text-red-800 text-sm font-medium"
                >
                  Try again
                </button>
              </div>
            )}

            {aiResult && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                    AI-POWERED
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">Deep Analysis</h2>
                </div>

                {/* Seniority + Company Insights */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white rounded-2xl shadow-lg border border-purple-200 p-6">
                    <h3 className="text-lg font-bold text-purple-800 mb-3">Seniority Level</h3>
                    <div className="bg-purple-50 border border-purple-200 rounded-xl px-5 py-3">
                      <span className="text-2xl font-extrabold text-purple-700">
                        {aiResult.seniorityLevel || 'Not specified'}
                      </span>
                    </div>
                  </div>
                  {aiResult.companyInsights && (
                    <div className="bg-white rounded-2xl shadow-lg border border-blue-200 p-6">
                      <h3 className="text-lg font-bold text-blue-800 mb-3">Company Insights</h3>
                      <p className="text-gray-700 leading-relaxed">{aiResult.companyInsights}</p>
                    </div>
                  )}
                </div>

                {/* Required vs Preferred Skills */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <SectionCard title={`Required Skills (${aiResult.requiredSkills.length})`} icon={CheckCircle2} variant="green">
                    {aiResult.requiredSkills.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {aiResult.requiredSkills.map((skill) => (
                          <TagBadge key={skill} text={skill} variant="green" />
                        ))}
                      </div>
                    ) : (
                      <EmptyState message="No required skills explicitly identified." />
                    )}
                  </SectionCard>

                  <SectionCard title={`Preferred Skills (${aiResult.preferredSkills.length})`} icon={Sparkles} variant="purple">
                    {aiResult.preferredSkills.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {aiResult.preferredSkills.map((skill) => (
                          <TagBadge key={skill} text={skill} variant="purple" />
                        ))}
                      </div>
                    ) : (
                      <EmptyState message="No preferred skills explicitly identified." />
                    )}
                  </SectionCard>
                </div>

                {/* Experience & Education */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {aiResult.experience.length > 0 && (
                    <SectionCard title="Experience Requirements" icon={Briefcase} variant="blue">
                      <ul className="space-y-2">
                        {aiResult.experience.map((exp, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                            <span className="text-blue-500 mt-0.5 flex-shrink-0">{'\u2022'}</span>
                            <span><strong>{exp.years}</strong> in {exp.field}</span>
                          </li>
                        ))}
                      </ul>
                    </SectionCard>
                  )}

                  {aiResult.education.length > 0 && (
                    <SectionCard title="Education Requirements" icon={GraduationCap} variant="blue">
                      <ul className="space-y-2">
                        {aiResult.education.map((edu, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                            <span className="text-blue-500 mt-0.5 flex-shrink-0">{'\u2022'}</span>
                            <span><strong>{edu.level}</strong> in {edu.field}</span>
                          </li>
                        ))}
                      </ul>
                    </SectionCard>
                  )}
                </div>

                {/* Key Responsibilities */}
                {aiResult.responsibilities.length > 0 && (
                  <SectionCard title={`Key Responsibilities (${aiResult.responsibilities.length})`} icon={ClipboardList} variant="gray">
                    <ul className="space-y-2">
                      {aiResult.responsibilities.map((resp, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                          <span className="bg-gray-200 text-gray-600 font-bold rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 text-xs mt-0.5">
                            {i + 1}
                          </span>
                          <span>{resp}</span>
                        </li>
                      ))}
                    </ul>
                  </SectionCard>
                )}
              </div>
            )}

            {/* CTA */}
            <ToolCTA
              headline="Now tailor your resume to match this job"
              description="Superpower Resume analyzes job descriptions and automatically tailors every section of your resume to match what the employer is looking for."
              buttonText="Create My Tailored Resume"
            />
          </div>
        )}

        {/* Educational Content */}
        <section className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mt-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">How to Decode a Job Description Like a Hiring Manager</h3>
          <div className="space-y-4 text-gray-700 leading-relaxed">
            <p>
              Job descriptions are more than just a list of requirements — they are a window into what an employer truly values. According to the{' '}
              <a href="https://www.shrm.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Society for Human Resource Management (SHRM)</a>,
              many job postings are written collaboratively between hiring managers and HR, which means the language often reflects competing priorities. Learning to read between the lines gives you a significant advantage.
            </p>
            <p>
              Start by separating <strong>must-have requirements</strong> from <strong>nice-to-haves</strong>. Must-haves typically appear under headings like &quot;Required Qualifications&quot; and use firm language such as &quot;must have&quot; or &quot;required.&quot; Nice-to-haves use softer phrasing like &quot;preferred,&quot; &quot;bonus,&quot; or &quot;ideally.&quot; Research from hiring platforms suggests that candidates who match 60-70% of listed requirements are still strong contenders, so do not be discouraged if you fall short on a few items.
            </p>
            <p>
              Pay attention to <strong>culture signals</strong> embedded in the posting. Phrases like &quot;fast-paced environment&quot; or &quot;wear many hats&quot; can indicate high workload expectations, while mentions of &quot;work-life balance,&quot; &quot;professional development,&quot; or &quot;mentorship&quot; suggest a more supportive culture. The{' '}
              <a href="https://www.onetonline.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">O*NET Online</a>{' '}
              database, maintained by the U.S. Department of Labor, is an excellent resource for understanding standardized job requirements and typical skills associated with any occupation.
            </p>
            <p>
              Finally, use the job description as a blueprint for your resume. Mirror the exact keywords and phrases the employer uses — this helps you pass Applicant Tracking Systems (ATS) and signals to human reviewers that you are a strong fit. Tailoring your resume to each specific posting remains one of the highest-impact job search strategies available.
            </p>
            <p className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-500">
              <strong>Sources &amp; Further Reading:</strong>{' '}
              <a href="https://www.bls.gov/ooh/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Bureau of Labor Statistics Occupational Outlook Handbook</a>
              {' · '}
              <a href="https://www.onetonline.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">O*NET Online — U.S. Department of Labor</a>
              {' · '}
              <a href="https://www.indeed.com/career-advice/finding-a-job/how-to-read-job-descriptions" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Indeed Career Advice: How to Read Job Descriptions</a>
            </p>
          </div>
        </section>

        {/* Pre-analysis CTA (only shown before analysis) */}
        {!hasAnalyzed && (
          <ToolCTA
            headline="Build a resume that matches any job description"
            description="Superpower Resume reads job descriptions and tailors your resume automatically. Get past ATS filters and impress hiring managers."
            buttonText="Get Started Free"
          />
        )}
      </div>
    </div>
  );
}
